import { FormControl, FormControlLabel, FormLabel } from '@mui/material';
import { RadioGroup, Radio, RadioGroupProps } from '@mui/material';
import React, { useState } from 'react';

export interface RadioOptionProps extends RadioGroupProps {
    value?: React.ReactNode;
    options: { label: string | React.ReactNode; value: string; disabled?: boolean; __optionKey?: string }[];
    label?: string | React.ReactNode;
    styleOverrides?: Record<string, Record<string, string>>; // set as {} to override custom sx
    // eslint-disable-next-line no-unused-vars
    onRadioChange?: (value: React.ReactNode) => void;
    selectedOptionId?: string; // Optional: for tracking which specific option is selected when multiple options have same value
    // eslint-disable-next-line no-unused-vars
    onOptionIdChange?: (optionId: string) => void; // Optional: callback when option ID changes
}

export const RadioOption = ({
    options,
    value,
    onRadioChange,
    label,
    styleOverrides,
    selectedOptionId,
    onOptionIdChange,
    ...props
}: RadioOptionProps) => {
    const [selectedValue, setSelectedValue] = useState(value);
    const [internalSelectedId, setInternalSelectedId] = useState<string>('');

    // Generate unique IDs for options if not provided
    const optionsWithIds = React.useMemo(() => {
        return options.map((option, index) => ({
            ...option,
            __internalId: option.__optionKey || `radio_option_${index}_${option.value}`
        }));
    }, [options]);

    // Determine which option should be selected based on unique ID or value
    const getSelectedOptionValue = () => {
        if (selectedOptionId || internalSelectedId) {
            const targetId = selectedOptionId || internalSelectedId;
            return targetId;
        }
        // If no specific ID is set, find the first option with matching value and return its ID
        const matchingOption = optionsWithIds.find((opt) => opt.value === value);
        return matchingOption?.__internalId || '';
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const clickedInternalId = (event.target as HTMLInputElement).value;
        const selectedOption = optionsWithIds.find((opt) => opt.__internalId === clickedInternalId);

        if (selectedOption) {
            setSelectedValue(selectedOption.value);
            onRadioChange?.(selectedOption.value);
            setInternalSelectedId(selectedOption.__internalId);
            onOptionIdChange?.(selectedOption.__internalId);
        }
    };

    React.useEffect(() => {
        setSelectedValue(value);

        // If selectedOptionId is provided externally, use it
        if (selectedOptionId) {
            setInternalSelectedId(selectedOptionId);
        } else if (value) {
            // Find the first option with matching value if no specific ID is provided
            const matchingOption = optionsWithIds.find((opt) => opt.value === value);
            if (matchingOption) {
                setInternalSelectedId(matchingOption.__internalId);
            }
        }
    }, [value, selectedOptionId, optionsWithIds]);

    return (
        <FormControl
            sx={
                styleOverrides ?? {
                    '.MuiFormControlLabel-root': {
                        '& .MuiTypography-root': {
                            fontSize: '1rem',
                            fontWeight: '600',
                            lineHeight: '1.25rem',
                            margin: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        },
                        '& .MuiButtonBase-root': {
                            padding: '3px 4px 3px 10px'
                        }
                    }
                }
            }
        >
            {label && (
                <FormLabel sx={{ marginBottom: '1rem' }} id="demo-radio-buttons-group-label">
                    {label}
                </FormLabel>
            )}
            <RadioGroup
                {...props}
                aria-labelledby="ret-goal-radio-buttons-group-label"
                value={getSelectedOptionValue()}
                name="radio-buttons-group"
                onChange={handleChange}
            >
                {optionsWithIds.map((option) => {
                    const isSelected =
                        selectedOptionId || internalSelectedId
                            ? option.__internalId === (selectedOptionId || internalSelectedId)
                            : option.value === selectedValue;

                    return (
                        <FormControlLabel
                            key={option.__internalId}
                            value={option.__internalId}
                            control={<Radio checked={isSelected} disabled={!!option.disabled} />}
                            label={option.label}
                        />
                    );
                })}
            </RadioGroup>
        </FormControl>
    );
};
